import { useDatasources } from '@commenda/api';
import {
  Button,
  FieldWrapper,
  Input,
  Label,
  SingleCombobox,
} from '@commenda/kakashi';
import {
  DataSourceType,
  FieldRefSource,
  TDataSourceCall,
  TWorkflowDefinition,
} from '@commenda/types';
import { cn } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { v4 as uuidV4 } from 'uuid';
import { z } from 'zod';
import { useNotification } from '../../../hooks/useNotification';
import { useForm } from '../../../hooks/useZodForm';
import { ParamValueEditor } from '../editors/ParamValueEditor';

const schema = z.object({
  dataSourceID: z.string().trim().min(1),
  alias: z.string().trim().min(1),
  params: z.record(z.string().trim(), z.any()),
});

type TAddEditDataSourceFormFields = z.infer<typeof schema>;

export const AddEditDataSourceForm = ({
  dataSource,
  onSave,
  formContentClassName,
  formFooterClassName,
  workflowDefinition,
  onCancel,
}: {
  dataSource: TDataSourceCall | null;
  onSave: (dataSource: TDataSourceCall) => void;
  workflowDefinition: TWorkflowDefinition;
  onCancel: () => void;
  formContentClassName?: string;
  formFooterClassName?: string;
}) => {
  const { setNotification } = useNotification();

  const { data: dataSourcesResponse } = useDatasources();
  const dataSources = dataSourcesResponse?.data ?? [];

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors: formErrors },
  } = useForm<TAddEditDataSourceFormFields>({
    resolver: zodResolver(schema),
    defaultValues: {
      dataSourceID: dataSource?.dataSourceID || '',
      params: dataSource?.params || {},
      alias: dataSource?.alias || '',
    },
  });

  const onSubmit = (data: TAddEditDataSourceFormFields) => {
    if (!data.params) {
      setNotification({
        title: 'Error',
        message: 'Params are required',
        show: true,
        valid: false,
      });
      return;
    }
    onSave({
      id: dataSource?.id || uuidV4(),
      type: DataSourceType.COMMENDA_LOGICAL_BACKEND,
      dataSourceID: data.dataSourceID,
      params: data.params,
      alias: data.alias,
      scope: 'per-step',
    });
  };

  return (
    <div>
      <div className={cn('flex flex-col gap-4', formContentClassName)}>
        <FieldWrapper>
          <Label>
            Data Source <span className="text-utility-violet-600">*</span>
          </Label>
          <SingleCombobox
            options={dataSources?.map((eachDataSource) => ({
              label: eachDataSource.name,
              value: eachDataSource.id,
            }))}
            value={watch('dataSourceID')}
            setValue={(value) => {
              setValue('dataSourceID', value);
            }}
            error={formErrors?.dataSourceID?.message}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Label>
            Alias <span className="text-utility-violet-600">*</span>
          </Label>
          <Input
            value={watch('alias')}
            onChange={(e) => setValue('alias', e.target.value)}
            error={formErrors?.alias?.message}
          />
        </FieldWrapper>

        <FieldWrapper>
          <Label>Params</Label>
          <ParamValueEditor
            value={watch('params')}
            onChange={(value) => {
              setValue('params', value);
            }}
            steps={workflowDefinition.steps || []}
            allowedFieldRefSources={[
              FieldRefSource.CONTEXT,
              FieldRefSource.STEP,
            ]}
          />
        </FieldWrapper>
      </div>
      <div
        className={cn('flex gap-3 justify-end border-t', formFooterClassName)}
      >
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit(onSubmit, (errors) => {
            console.log('errors', errors);
          })}
        >
          Save
        </Button>
      </div>
    </div>
  );
};
